import React, { createContext, useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { Box, Button, Link, Checkbox, TextField, FormControlLabel, Typography } from '@mui/material';
import { UserContext } from 'components/UserGuard';
import Modal from 'components/shared/Modal';
import * as ProfilesService from 'services/api/ProfilesService';
import { trackEvent } from 'util/gtm';

interface VerifyEmailButtonProps {
  email: string;
  disabled?: boolean;
  exitEditing?: () => void;
}
export const VerifyEmailButton: React.FunctionComponent<VerifyEmailButtonProps> = ({email, disabled = false, exitEditing}) => {
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [code, setCode] = React.useState(['', '', '', '', '', '']);
  const inputRefs = useRef<Array<HTMLInputElement>>([]);


  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newCode = [...code];
    newCode[index] = event.target.value;
    setCode(newCode);

    // Automatically focus the next input if a character is entered
    if (event.target.value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle backspace to clear and move focus back
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = e.clipboardData.getData('Text');
    const newCode = pastedValue.split("").slice(0,6);
    if (newCode.length < 6){
       while (newCode.length < 6){
        newCode.push('')
      }
    }


    setCode(newCode);

    e.preventDefault();
  };

  const openModal = useCallback(async () => {
    setLoading(true)
    try{
      const nccerCardNumber = user.nccerCardNumber ?? '';
      await ProfilesService.sendEmailVerificationCode(nccerCardNumber, email, "email")
      trackEvent('email_verification_get_code');
      setLoading(false)
    } catch (error){
      //handle error here
      setLoading(false)
    }
    setOpen(true);
  }, [email]);
  const closeModal = useCallback(() => setOpen(false), []);
  
  const onSubmit = useCallback(async () => {
    setLoading(true)
    try{
      const nccerCardNumber = user.nccerCardNumber ?? '';
      await ProfilesService.validateEmailCode(nccerCardNumber, email, code.join(""))
      trackEvent('email_verification_verify_code');
      setLoading(false)
      exitEditing && exitEditing();
      setOpen(false)

    } catch (error){
      //handle error here
      setLoading(false)
    }}, [email, code]
  );

  const resendCode = useCallback(async () => {
    setLoading(true)
    try{
      const nccerCardNumber = user.nccerCardNumber ?? '';
      await ProfilesService.sendEmailVerificationCode(nccerCardNumber, email, "email")
      //send code here
      setLoading(false)
    } catch (error){
      //handle error here
      setLoading(false)
    }
  }, [email])

  return (
    <EmailModalContext.Provider value={{ setLoading, closeModal }}>
      <Button
        variant="outlined"
        color="secondary"
        disabled={loading || disabled}
        onClick={openModal}
        sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, height: 'fit-content', minHeight: '56px', margin: 'auto', marginTop: "16px"}}
      >
        <Box display={{ xs: 'inline', sm: 'inline', md: 'inline', minWidth: "92px", padding: "0", paddingBottom: "0"}}>Get Code</Box>
      </Button>
      <Modal 
        loading={loading} 
        open={open} 
        title={"Verify Your Email"} 
        subTitleText={''} 
        onClose={closeModal} 
        actions={
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'right', width: "100%" }}>
            <Button variant="outlined" color="secondary" onClick={closeModal} sx={{marginRight: "15px"}}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={onSubmit} disabled={code.some((e) => e ==='')}>
              Verify
            </Button>
          </Box>
        }
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            Please check your Email, we've sent a code to 
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
            {email}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: '10px' }}>
          {code.map((char, index) => (
            <Box sx={{ display: 'flex', justifyContent: 'center', margin: '5px' }}>
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={char}
                onChange={(e) => handleChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handleOnPaste}
                inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                sx={{ width: 50 }} // Adjust width as needed
                variant="outlined"
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginTop: '15px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            Didn't get a code? 
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
            <Link onClick={resendCode}>Click here to resend</Link>
          </Box>
        </Box>
      </Modal>
    </EmailModalContext.Provider>
  );
}

interface EmailModalContext {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const EmailModalContext = createContext<EmailModalContext>({
  setLoading: () => undefined,
  closeModal: () => undefined,
});